import { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { Expense, ResponseWarning } from '../types';
import { getMonthDateRange } from '../utils/formatters';

export const useExpenses = (currency: string, selectedDate: Date) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [warnings, setWarnings] = useState<ResponseWarning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadExpenses = async () => {
      try {
        setLoading(true);
        setError(null);
        const { startDate, endDate } = getMonthDateRange(selectedDate);
        const data = await apiService.getExpenses(currency, startDate, endDate);
        if (!cancelled) {
          setExpenses(data.expenses);
          setWarnings(data.warnings || []);
        }
      } catch (err: any) {
        if (cancelled || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err as Error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadExpenses();

    return () => {
      cancelled = true;
    };
  }, [currency, selectedDate]);

  const reload = async () => {
    try {
      setLoading(true);
      setError(null);
      const { startDate, endDate } = getMonthDateRange(selectedDate);
      const data = await apiService.getExpenses(currency, startDate, endDate);
      setExpenses(data.expenses);
      setWarnings(data.warnings || []);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  return { expenses, warnings, loading, error, reload };
};
